import styled from "styled-components";
import { WidgetWrapper } from "../../../../style/DashbordWidgetWrappers";
import WidgetHeader from "../../../Widgets/WidgetHeader";
import { useTranslation } from "react-i18next";
import { Users } from "../../../../redux/apiSpecifications/apiFesf";
import { MaterialReactTable, MRT_ColumnDef } from "material-react-table";
import { useEffect, useMemo, useState } from "react";
import { UserItem } from "../UserItem";
import { CellWrapper, ClickableCellWrapper } from "../style/CellWrapper";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import { MRT_Localization_IT } from "material-react-table/locales/it";
import { MRT_Localization_EN } from "material-react-table/locales/en";

const AccountsToManageListWrapper = styled(WidgetWrapper)`
  flex-direction: column;
  align-items: stretch;
  max-height: 2.5em;
  transition: max-height 1s;
  &.open {
    max-height: 100em;
  }
`;

export const CellRolesWrapper = styled.div`
  display: flex;
  column-gap: 1em;
`;

interface IAccountsToManageListProps {
  users: Users;
  onEditUserContact: (userItem: UserItem) => void;
  onEditUserConfig: (userItem: UserItem) => void;
  onEditUserCollaboratorConfig: (userItem: UserItem) => void;
}

const AccountsToManageList = (props: IAccountsToManageListProps) => {
  const { t } = useTranslation();

  const { activeRole, language } = useSelector(
    (state: RootState) => state.user
  );

  const {
    users,
    onEditUserContact,
    onEditUserConfig,
    onEditUserCollaboratorConfig,
  } = props;

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 5,
  });

  const [isOpen, setIsOpen] = useState(users!.length > 0);

  useEffect(() => {
    setIsOpen(users!.length > 0);
  }, [users]);

  const data = useMemo(
    () =>
      users?.map((user) => {
        return {
          id: user.id,
          idSubject: user.idSubject,
          firstname: user.firstname,
          surname: user.surname,
          account: user.userName,
          loginCode: user.loginCode,
          isCollaborator: user.isCollaborator,
          appraisalStudioId: user.appraisalStudioId,
          appraisalStudio: user.appraisalStudio,
          email: user.email,
          contact: user.firstname + " " + user.surname,
          roles: user.roles,
          rolesText: user.roles
            ?.map((r) => t(`role-description-${r.roleCode!}`))
            .join(", "),
          tenants: user.tenants,
          tenantsText: user.tenants?.map((t) => t.companyName).join(", "),
          isActive: user.isActive,
          userConfigLists: {
            allRoles: user.roles,
            allTenants: user.tenants,
          },
        } as UserItem;
      }) || [],
    [users]
  );

  const handleSelectContanct = (row: UserItem) => {
    onEditUserContact(row);
  };

  const handleSelectConfig = (row: UserItem) => {
    onEditUserConfig(row);
  };

  const handleSelectCollaboratorConfig = (row: UserItem) => {
    onEditUserCollaboratorConfig(row);
  };

  const columns = useMemo<MRT_ColumnDef<UserItem>[]>(
    () => [
      {
        accessorKey: "account",
        header: t("accounts-to-manage-list-account-column-header"),
        size: 100,
        filterFn: "contains",
        Cell: ({ row }) => <CellWrapper>{row.original.account}</CellWrapper>,
      },
      {
        accessorKey: "contact",
        header: t("accounts-to-manage-list-contact-column-header"),
        size: 200,
        filterFn: "contains",
        Cell: ({ row }) => (
          <ClickableCellWrapper
            onClick={() => handleSelectContanct(row.original)}
          >
            {row.original.idSubject !== null
              ? row.original.contact
              : t("accounts-to-manage-list-complete-registry-placeholder")}
          </ClickableCellWrapper>
        ),
      },
      {
        accessorKey: "loginCode",
        header: t("accounts-to-manage-list-login-code-column-header"),
        size: 200,
        filterFn: "contains",
        Cell: ({ row }) => (
          <ClickableCellWrapper
            onClick={() => handleSelectContanct(row.original)}
          >
            {row.original.loginCode}
          </ClickableCellWrapper>
        ),
      },
      {
        accessorKey: "roles",
        header: t("accounts-to-manage-list-roles-column-header"),
        size: 400,
        filterFn: "",
        Cell: ({ row }) => {
          if (activeRole === "C1") {
            return (
              <ClickableCellWrapper
                onClick={() => handleSelectConfig(row.original)}
              >
                {row.original.rolesText !== ""
                  ? row.original.rolesText
                  : t("accounts-to-manage-list-select-roles-placeholder")}
              </ClickableCellWrapper>
            );
          }

          return (
            <CellRolesWrapper>
              <ClickableCellWrapper
                onClick={() => handleSelectConfig(row.original)}
              >
                {row.original.rolesText !== ""
                  ? row.original.rolesText
                  : t(
                      "accounts-to-manage-list-select-standard-roles-placeholder"
                    )}
              </ClickableCellWrapper>

              <ClickableCellWrapper
                onClick={() => handleSelectCollaboratorConfig(row.original)}
              >
                {row.original.rolesText !== ""
                  ? row.original.rolesText
                  : t(
                      "accounts-to-manage-list-select-appraiser-collaborator-placeholder"
                    )}
              </ClickableCellWrapper>
            </CellRolesWrapper>
          );
        },
      },
      {
        accessorKey: "tenants",
        header: t("accounts-to-manage-list-customer-management-column-header"),
        size: 400,
        maxSize: 9001,
        filterFn: "contains",
        Cell: ({ row }) => (
          <ClickableCellWrapper
            onClick={() => handleSelectConfig(row.original)}
          >
            {row.original.tenantsText !== ""
              ? row.original.tenantsText
              : t("accounts-to-manage-list-select-tenants-placeholder")}
          </ClickableCellWrapper>
        ),
      },
    ],
    [users]
  );

  const handleOnOpen = () => setIsOpen(true);
  const handleOnClose = () => setIsOpen(false);

  return (
    <AccountsToManageListWrapper className={`${isOpen ? "open" : ""}`}>
      <WidgetHeader
        title={t("accounts-to-manage-title")}
        isActive={false}
        onOpen={handleOnOpen}
        onClose={handleOnClose}
        isOpen={isOpen}
      />

      {/* <pre>{JSON.stringify(activeRole, null, 2)}</pre> */}

      <div className="body">
        {data.length > 0 && (
          <MaterialReactTable
            muiTablePaperProps={{ style: { width: "100%" } }}
            columns={columns}
            data={data}
            enableDensityToggle={false}
            enableColumnOrdering
            localization={
              language === "it" ? MRT_Localization_IT : MRT_Localization_EN
            }
            muiTableBodyCellProps={{
              sx: {
                textAlign: "left",
              },
            }}
            onPaginationChange={setPagination}
            state={{ pagination }}
            autoResetPageIndex={false}
          />
        )}
      </div>
    </AccountsToManageListWrapper>
  );
};

export default AccountsToManageList;
